import * as React from 'react'
import { connect } from 'react-redux'
import * as commonActions from 'common/actions'
import {
  AprType,
  filterStyleCompact,
  LoanOfferListItem,
  LoanOfferProductSubType,
  LoanRatesFilter,
  OfferOverflowList,
} from 'embed/constants'
import { unfilteredRatesByLenderSelector } from 'embed/selectors'
import Parent from 'common/services/Parent'
import { LoanOffer } from 'embed/constants/models'

import ErrorIndicator from 'embed/components/ErrorIndicator'
import DefaultLoadingIndicator, { LoadingIndicatorProps } from 'embed/components/LoadingIndicator'

import PaginateOffers from 'embed/components/PaginateOffers'
import {
  configCompanyUuidSelector,
  configFeatureFlagsSelector,
  configOfferOverflowSelector,
  isDotDashSelector,
} from 'embed/selectors/config'
import { loadingSelector } from 'embed/selectors/loading'
import { captureMultipleProductSubTypeRateTable } from 'embed/constants/errors'
import { MultiProductState } from 'embed/reducers'
import { uiPreviewIsInitializingSelector } from 'embed/selectors/ui'
import {
  FeatureFlag,
  Filter,
  LoanPurposeOptions,
  LoanSortOption,
  OfferType,
  Product,
  ProductResponseKeys,
  QueryParamKeys,
  RedirectType,
  SortDirection,
} from 'even-ts-static'
import { LoanOfferList } from './index'
import {
  AprVsRecSorting,
  Experiment,
  LoanOffersGroupedBySubType,
  Original,
  SortByClosestLoanAmount,
  Variant,
  withExperiment,
} from 'ui/multi-product/experiments'
import { GroupedLoanOfferList } from 'embed/components/LoanOfferList/index.grouped'
import { Loan } from 'embed/components/Loan/Application/loan-variant'
import { loanVariantSelector } from 'embed/selectors/application'

export type LoanOffersByLender = { [s: string]: LoanOffer[] }

export interface LoanOfferListStyles {
  wrapper: string
}
export interface LoanOfferListContainerStoreProps {
  allLoanOffers?: readonly LoanOffer[]
  allLoanOffersByLender: LoanOffersByLender
  companyUuid: string
  currentStep: string
  error: boolean
  groupResultsByProductSubType: boolean
  isDotDash: boolean
  loading: boolean
  loanOffers: LoanOffer[]
  loanPurpose?: LoanPurposeOptions
  loanVariant: Loan
  offerOverflow?: OfferOverflowList
  pageSize?: number
  ratesFilters?: { [key in LoanRatesFilter]?: string[] }
  rateTableUuid?: string
  sortDirection: SortDirection
  sortOption: LoanSortOption
  viewportHeight?: number
  viewportWidth?: number
}

interface DispatchProps {
  dispatchGetLoanOffer?: (offer: LoanOffer) => null
  dispatchLoanOfferPreviewSelected?: (offer: LoanOffer) => null
  dispatchRatesFilterChanged: (filterType: LoanRatesFilter, filterValue: unknown, replace: boolean) => void
  dispatchSeeMoreFromLender: (lenderId: string, lenderName: string) => void
  dispatchSortChanged?: (sortOption: LoanSortOption, sortDirection: SortDirection) => null
}

export interface OwnProps {
  allLoanOffersByLender: LoanOffersByLender
  bestOffer: LoanOffer
  limit?: number
  listItem: LoanOfferListItem<LoanOffer>
  loadingIndicator: React.ComponentType<LoadingIndicatorProps>
  brandColor?: string
  ctaButtonText: string
  ctaPreQualButtonText: string
  ctaLabel: string
  dispatchOfferCta: (loan: LoanOffer, redirectType: RedirectType) => void
  isResultsView: boolean
  showDefaultSortOption?: boolean
  showSeeMoreOffers?: boolean
  selectedAprVsRecSortingVariantId?: string
  selectedLoanOffersGroupedBySubTypeVariantId?: string
  selectedSortByClosestLoanAmountVariantId?: string
}

export type Props = LoanOfferListContainerStoreProps & DispatchProps & OwnProps

const offersExist = (allLoanOffers, aprType) => allLoanOffers?.find((offer) => offer.aprType === aprType)

const aprFilterEnabled = (ratesFilters, aprType) => ratesFilters?.[LoanRatesFilter.AprType]?.indexOf(aprType) !== -1

const hasNonSLROffers = (allLoanOffers) =>
  allLoanOffers?.filter((offer) => offer.productSubType !== LoanOfferProductSubType.StudentLoanRefinance).length

const slrFilterEnabled = (ratesFilters) =>
  ratesFilters?.[LoanRatesFilter.ProductSubType]?.indexOf(LoanOfferProductSubType.StudentLoanRefinance) !== -1

const rateTableWithMixedSubtypeOffers = (allLoanOffers) =>
  allLoanOffers?.find(
    (offer) =>
      offer.productSubType !== LoanOfferProductSubType.StudentLoanRefinance &&
      offer.productSubType !== LoanOfferProductSubType.PersonalLoan,
  )

const Container: React.FC<Props> = ({
  allLoanOffers,
  allLoanOffersByLender,
  bestOffer,
  brandColor,
  companyUuid,
  currentStep,
  dispatchOfferCta,
  dispatchRatesFilterChanged,
  dispatchSeeMoreFromLender,
  dispatchSortChanged,
  error,
  isDotDash,
  isResultsView,
  limit,
  loading,
  loadingIndicator,
  loanOffers,
  loanVariant,
  offerOverflow,
  pageSize,
  ratesFilters,
  rateTableUuid,
  sortDirection,
  sortOption,
  ...props
}) => {
  const offerListRef = React.useRef<HTMLOListElement>(null)

  React.useEffect(() => {
    if (!allLoanOffers?.length || loading) {
      return
    }
    const noFixedOffers = !offersExist(allLoanOffers, AprType.Fixed)
    const noVariableOffers = !offersExist(allLoanOffers, AprType.Variable)

    const noOffersByFilter =
      (noFixedOffers && aprFilterEnabled(ratesFilters, AprType.Fixed)) ||
      (noVariableOffers && aprFilterEnabled(ratesFilters, AprType.Variable))

    if (noOffersByFilter) {
      dispatchRatesFilterChanged(LoanRatesFilter.AprType, noFixedOffers ? AprType.Variable : AprType.Fixed, true)
    }
    if (!hasNonSLROffers(allLoanOffers) && !slrFilterEnabled(ratesFilters)) {
      dispatchRatesFilterChanged(LoanRatesFilter.ProductSubType, LoanOfferProductSubType.StudentLoanRefinance, true)
    } else if (rateTableWithMixedSubtypeOffers(allLoanOffers)) {
      if (isResultsView) {
        captureMultipleProductSubTypeRateTable(rateTableUuid ?? '')
      } else {
        captureMultipleProductSubTypeRateTable('preview')
      }
    }
  }, [allLoanOffers, loading, ratesFilters, rateTableUuid, isResultsView])

  const onSortOptionClick = React.useCallback(
    (updatedSortOption: LoanSortOption) => {
      if (updatedSortOption === sortOption) {
        // If they clicked the same sort, reverse it
        const updatedSortDirection = sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc
        dispatchSortChanged?.(updatedSortOption, updatedSortDirection)
      } else {
        dispatchSortChanged?.(updatedSortOption, SortDirection.Asc)
      }
    },
    [sortOption, sortDirection, dispatchSortChanged],
  )

  const onSeeMoreFromLenderClick = React.useCallback(
    (offer: LoanOffer) => {
      Parent.scrollToFrame(offerListRef.current?.offsetTop)

      const lenderId = offer.originator?.companyUuid ?? ''
      const lenderName = offer.originator?.name ?? ''
      dispatchSeeMoreFromLender(lenderId, lenderName)
    },
    [dispatchSeeMoreFromLender],
  )

  const LoadingIndicator = loadingIndicator ?? DefaultLoadingIndicator

  if (error) {
    return <ErrorIndicator />
  } else if (loading) {
    return (
      <LoadingIndicator
        brandColor={brandColor}
        loadingLabel="Finding Loan Offers"
        showPendingOriginators={true}
        product={Product.Loan}
      />
    )
  } else {
    return (
      <PaginateOffers
        enabled={offerOverflow === OfferOverflowList.PAGINATE}
        offers={loanOffers.slice(0, limit ?? 8)}
        offerListRef={offerListRef}
        pageSize={pageSize}
      >
        {({ offersPage, paginationControls }) => (
          <React.Fragment>
            <Experiment experiment={LoanOffersGroupedBySubType}>
              <Original>
                <LoanOfferList
                  companyUuid={companyUuid}
                  allLoanOffersByLender={allLoanOffersByLender}
                  bestOfferId={bestOffer?.uuid}
                  currentStep={currentStep}
                  dispatchOfferCta={dispatchOfferCta}
                  displaySortBarItems={!filterStyleCompact()}
                  isDotDash={isDotDash}
                  isResultsView={isResultsView}
                  offerListRef={offerListRef}
                  onSeeMoreFromLenderClick={onSeeMoreFromLenderClick}
                  onSortOptionClick={onSortOptionClick}
                  sortDirection={sortDirection}
                  loanOffers={offersPage as LoanOffer[]}
                  sort={sortOption}
                  {...props}
                />
              </Original>
              <Variant
                experiment={LoanOffersGroupedBySubType}
                variant={LoanOffersGroupedBySubType.variants.GroupedOffers}
              >
                <GroupedLoanOfferList
                  allLoanOffersByLender={allLoanOffersByLender}
                  companyUuid={companyUuid}
                  bestOfferId={bestOffer?.uuid}
                  currentStep={currentStep}
                  dispatchOfferCta={dispatchOfferCta}
                  displaySortBarItems={!filterStyleCompact()}
                  isDotDash={isDotDash}
                  isResultsView={isResultsView}
                  loanOffers={offersPage as LoanOffer[]}
                  loanVariant={loanVariant}
                  offerListRef={offerListRef}
                  onSeeMoreFromLenderClick={onSeeMoreFromLenderClick}
                  onSortOptionClick={onSortOptionClick}
                  sortDirection={sortDirection}
                  sort={sortOption}
                  {...props}
                />
              </Variant>
            </Experiment>
            {paginationControls}
          </React.Fragment>
        )}
      </PaginateOffers>
    )
  }
}

const mapStateToProps = (state: MultiProductState, ownProps): LoanOfferListContainerStoreProps => {
  const groupResultsByProductSubType = configFeatureFlagsSelector(state).includes(FeatureFlag.LoanResultsGrouping)
  const loanOffers = ownProps.loanOffers || state.rates.sorted[Product.Loan]?.loanOffers

  return {
    allLoanOffers: state.rates[Product.Loan].loanOffers ?? [],
    allLoanOffersByLender:
      ownProps.allLoanOffersByLender !== undefined
        ? ownProps.allLoanOffersByLender
        : unfilteredRatesByLenderSelector(state, {
            offersPath: `${Product.Loan}.${ProductResponseKeys[Product.Loan]}Offers`,
            product: Product.Loan,
          }),
    companyUuid: configCompanyUuidSelector(state) ?? '',
    currentStep: state.ui.selectedStep[Product.Loan],
    error: !!state.loading[Product.Loan]?.error,
    groupResultsByProductSubType,
    isDotDash: isDotDashSelector(state),
    loading: loadingSelector(state, Product.Loan) || uiPreviewIsInitializingSelector(state),
    loanOffers,
    loanPurpose: state.filters[Product.Loan]?.[Filter.LoanPurpose],
    loanVariant: loanVariantSelector(state),
    offerOverflow: configOfferOverflowSelector(state),
    pageSize: state.config[QueryParamKeys.PageSize],
    rateTableUuid: state.rates[Product.Loan].uuid,
    ratesFilters: state.rates.filters[Product.Loan],
    sortDirection: state.rates.sortDirection[Product.Loan],
    sortOption: state.rates.sortOption[Product.Loan],
    viewportHeight: state.ui.viewport.height,
    viewportWidth: state.ui.viewport.width,
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchGetLoanOffer: (offer: LoanOffer) =>
    dispatch(commonActions.redirectToOffer(offer, OfferType.Loan, RedirectType.MANUAL)),
  dispatchLoanOfferPreviewSelected: (offer: LoanOffer) => dispatch(commonActions.loanOfferPreviewSelected(offer)),
  dispatchRatesFilterChanged: (filterType: LoanRatesFilter, filterValue: unknown, replace: boolean) =>
    dispatch(commonActions.ratesFilterChanged(Product.Loan, filterType, filterValue, replace)),
  dispatchSeeMoreFromLender: (lenderId: string, lenderName: string) =>
    dispatch(commonActions.seeMoreFromLender(Product.Loan, lenderId, lenderName)),
  dispatchSortChanged: (sortOption, sortDirection) =>
    dispatch(commonActions.sortChanged(sortDirection, Product.Loan, sortOption)),
})

const ConnectedContainer = connect<LoanOfferListContainerStoreProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(Container)

export const LoanOfferListContainer = withExperiment(LoanOffersGroupedBySubType, {
  selectedVariantPropName: 'selectedLoanOffersGroupedBySubTypeVariantId',
})(
  withExperiment(AprVsRecSorting, {
    selectedVariantPropName: 'selectedAprVsRecSortingVariantId',
  })(
    withExperiment(SortByClosestLoanAmount, {
      selectedVariantPropName: 'selectedSortByClosestLoanAmountVariantId',
    })(ConnectedContainer),
  ),
)
